// Act IV scenario content — 가시 궁정. Stages 11–13: 드라벤 → 실렌 → 카이로스.
// The first VILLAIN act: the enemy opens (playerDisc:"light", 후공), and the
// scenes are about the villains' goals and the forest's fate — no "시험"
// framing. Beats: othellos/CAMPAIGN_NARRATIVE_BEATS.md rows 11–13 (매복·봉쇄 /
// 기록 탈환 / 동료 구출). All three stages (드라벤 / 실렌 / 카이로스) are written;
// 카이로스 is the act-completion node — beyond his 사냥문 lies 막 V. Every
// narration/dialogue string is a `Loc`; names stay Korean (see act1.ts's
// comment / ./loc.ts).

import type { DialogueLine, PinNode } from "./act1.ts";
import type { Loc } from "./loc.ts";

export const ACT4_NAME: Loc = { ko: "IV. 가시 궁정", en: "IV. The Thorn Court" };

export const DRABEN_ID = 11;
export const SILEN_ID = 12;
export const KAIROS_ID = 13;

export const ACT4_BG = "/assets/backgrounds/act-4-thorn-court-v1.webp";
export const ACT4_MAP_BG = "/assets/world-maps/act-4-thorn-court.webp";

export const DRABEN_PORTRAIT =
  "/assets/characters/11-draven-red-lance-rival-v1.webp";
export const SILEN_PORTRAIT =
  "/assets/characters/12-silen-corrupt-librarian-rival-v1.webp";
export const KAIROS_PORTRAIT =
  "/assets/characters/13-kairos-dusk-hunter-rival-v1.webp";

/** ACT3_TO_ACT4 — bridge narration after 로웬's verdict: the party uses the
 *  통행권 to cross into 가시 궁정, finds it wrapped in red rift-light and black
 *  thorn, and the tone turns — no more trials, everyone here is 벨라's, and
 *  드라벤 holds the entrance. Mirrors ACT2_TO_ACT3_LINES; `lastHint` points on
 *  to 드라벤. */
export const ACT3_TO_ACT4_LINES: readonly Loc[] = [
  { ko: "로웬의 통행권을 들고 가시 궁정의 문을 넘었다.", en: "With Rowen's passage token, we crossed into the Thorn Court." },
  { ko: "안은 붉은 균열빛과 검은 가시로 뒤덮여 있었다.", en: "Inside, everything was choked in red rift-light and black thorns." },
  { ko: "여기서부터 시험은 없다. 마주치는 자는 모두 벨라의 편이다.", en: "There are no trials from here on. Everyone we meet stands with Vela." },
  { ko: "문을 통과하자 앞을 처음 막아선 건 붉은 창의 기사, 드라벤이다.", en: "The moment we passed the gate, Draven, the Knight of the Red Lance, barred our way." },
];

/** ACT4_MAP — three nodes up the thorned court path, near to far: 드라벤 at the
 *  gate the party just crossed, 실렌 deeper in at the 수정 기록실, 카이로스 at
 *  the castle core (the act-completion node). First-pass coords over
 *  `act-4-thorn-court.webp` — tune in-browser to the path/arch. */
export const ACT4_PINS: readonly PinNode[] = [
  { id: 11, name: "드라벤", title: "붉은 창의 기사", pos: { x: 0.5, y: 0.83 }, scale: 1.25 },
  { id: 12, name: "실렌", title: "수정 사서", pos: { x: 0.5, y: 0.61 }, scale: 0.85 },
  { id: 13, name: "카이로스", title: "황혼의 사냥꾼", pos: { x: 0.46, y: 0.44 }, scale: 0.62 },
];

/** STAGE11_DRABEN_MEET — 벨라's 선봉장 stops the party at the entrance and waves
 *  off 로웬's 통행권. A blunt authoritarian enforcer, not a mystic: the 왕비's
 *  word is the only law here, and anyone opposing her is arrested — crossing the
 *  gate at all counts as opposition. 브람 tries to talk their way past (his
 *  role); 드라벤 doesn't buy it — 벨라's court already knows who they are.
 *  Surrender to the lance, or break through. First villain: no "시험".
 *  (Player is 백/후공 — 드라벤 opens.) */
export const DRABEN_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "드라벤", text: { ko: "거기 서라. 로웬의 종이 쪼가리를 흔들 생각이면 접어 둬.", en: "Stop right there. If you're waving Rowen's scrap of paper, put it away." } },
  { speaker: "opp", name: "드라벤", text: { ko: "이 안에서 통하는 건 왕비의 명 하나다. 균열의 왕비에게 반대하는 자는 전부 체포한다.", en: "Only the Queen's word carries weight in here. Anyone who opposes the Rift Queen gets arrested." } },
  { speaker: "opp", name: "브람", text: { ko: "우린 반대하러 온 게 아니야. 지나가려는 것뿐이지.", en: "We're not here to oppose anyone. We just want to pass." } },
  { speaker: "opp", name: "드라벤", text: { ko: "우리의 정보력을 너무 우습게 보는군. 순순히 창을 받든가, 뚫고 가든가.", en: "You underestimate how much we already know. Submit peacefully, or force your way through." } },
  { speaker: "player", name: "나", text: { ko: "그럼 뚫고 가지.", en: "Then I'll break through." } },
];

/** STAGE11_DRABEN_OUTRO — the first 방벽 falls. 드라벤 is shaken his 창 couldn't
 *  break the player, but stays defiant. 토르 names what's next — 실렌 in the
 *  기록의 방, and (given the court's fast comms, cf. DRABEN_MEET's "정보력")
 *  already forewarned that 드라벤 lost — so 세라 pushes to move fast. 드라벤 does
 *  not join (villain). */
export const DRABEN_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "드라벤", text: { ko: "내 창이… 내가 뚫리다니…", en: "My lance... and I'm the one who's broken..." } },
  { speaker: "opp", name: "드라벤", text: { ko: "첫 방벽이 무너졌다고 끝난 게 아니다.", en: "One wall falling isn't the end of this." } },
  { speaker: "opp", name: "토르", text: { ko: "기록의 방엔 실렌이 있다. 그도 드라벤이 패한 사실을 들었을 거야.", en: "Silen's in the records room. She'll have already heard Draven lost." } },
  { speaker: "opp", name: "세라", text: { ko: "이제 입구는 뚫렸어. 기록의 방으로 가자 — 이번엔 우리가 먼저 도착해야 해.", en: "The entrance is open now. Let's move to the records room — this time we need to get there first." } },
];

/** STAGE12_SILEN_MEET — the 수정 사서 doesn't just archive past duels: she claims
 *  she *fixes the future*, writing the one ending the 왕비's book allows. To take
 *  their choices back the player has to rewrite that ending on the board. 린 (the
 *  contemplative one) is the philosophical foil. Villain: no "시험". (Player is
 *  백/후공 — 실렌 opens.) */
export const SILEN_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "실렌", text: { ko: "왔구나. 너희의 미래는 전부 여기, 수정책 안에 있다. 결과도 기억도, 이제 내가 정한 대로 이루어지는 것이지.", en: "You've come. Your futures are all right here, in the crystal book. Outcomes, memories — now they play out however I've decided." } },
  { speaker: "opp", name: "린", text: { ko: "사람의 미래를 책에 가둘 순 없어요.", en: "You can't lock a person's future in a book." } },
  { speaker: "opp", name: "실렌", text: { ko: "선택은 혼란이야. 왕비의 책엔 결말이 하나면 돼 — 나는 그 하나를 적는다.", en: "Choice is chaos. The Queen's book needs only one ending — I'm the one who writes it." } },
  { speaker: "player", name: "나", text: { ko: "결말을 정할 권리는 너한테도 왕비한테도 없어. 우리 선택과 미래를 돌려받겠다.", en: "Neither you nor the Queen has the right to decide our ending. We're taking our choices and our future back." } },
  { speaker: "opp", name: "실렌", text: { ko: "그럼 네 다음 줄을 여기서 지워 주마.", en: "Then let me erase your next line, right here." } },
];

/** STAGE12_SILEN_OUTRO — 실렌 doesn't help. Bitter and stunned, she rages that
 *  her one-ending world is broken and never explains 벨라's plan. The party
 *  pieces it together, each line answering the one before it instead of
 *  taking parallel turns at exposition (v2, fixed a "따로 노는" polish-pass
 *  finding): 세라 reacts to the book itself, 린 corrects her word ("적힌 게
 *  아니라 가둔 거예요") and folds in 벨라's endgame (carve it into the 봉인의
 *  심장), 토르 picks up 린's exact word "심장" to bridge to 카이로스 at the
 *  심장부 문 and his creed (solidarity is weakness, cut one loose); 실렌's
 *  parting taunt is that the split *is* the one ending, which the player
 *  rejects outright. Villain. */
export const SILEN_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "실렌", text: { ko: "이건 있을 수 없는 일이야. 이 책엔 결말이 하나뿐이어야 한다고!", en: "This can't happen. This book is only supposed to have one ending!" } },
  { speaker: "opp", name: "세라", text: { ko: "이 책… 우리가 뒀던 결투가 전부 여기 있었어.", en: "This book... every duel we've fought is in here." } },
  { speaker: "opp", name: "린", text: { ko: "적힌 게 아니라 가둔 거예요. 벨라는 이걸 봉인의 심장에 새겨서, 아무도 결말을 못 바꾸게 하려는 거고요.", en: "It's not written — it's a cage. Vela means to carve this into the Seal Heart so no one can ever change the ending again." } },
  { speaker: "opp", name: "토르", text: { ko: "그 심장부 문은 카이로스가 쥐고 있어. 연대는 약점이라면서, 우리 중 하나를 끊어내려 들 거야.", en: "Kairos holds the door to the heart chamber. He calls solidarity a weakness — he'll try to cut one of us loose." } },
  { speaker: "opp", name: "실렌", text: { ko: "너희는 갈라지게 돼 있어. 그게 하나의 결말이라고!", en: "You're meant to split apart. That's the one ending!" } },
  { speaker: "player", name: "나", text: { ko: "우린 안 갈라져. 한 명도 안 버려.", en: "We won't split. Not one of us gets left behind." } },
];

/** STAGE13_KAIROS_MEET — the party reaches the 사냥문 to find 린 already across
 *  the line. 카이로스 never took her; he talked her into walking over — three may
 *  pass, the fourth is a closed "계약". He stays procedural (people as settled
 *  accounts), 브람 catches that 린 is just echoing his words back, 린 deflects
 *  rather than defends. The player rejects the 계약 frame outright and plays to
 *  undo her choice. Villain: no "시험". (Player is 백/후공 — 카이로스 opens.) */
export const KAIROS_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "카이로스", text: { ko: "이 통로를 지날 수 있는 사람은 셋까지다. 나머지 하나는 계약이 끝났고.", en: "Only three may pass through this corridor. The fourth's contract has already ended." } },
  { speaker: "opp", name: "세라", text: { ko: "린. 너 지금 무슨 짓을 한 거야.", en: "Rin. What have you done." } },
  { speaker: "opp", name: "린", text: { ko: "…이 방법밖에 없었어요.", en: "...It was the only way." } },
  { speaker: "opp", name: "브람", text: { ko: "그거 네 말이야, 저 사람 말이야?", en: "Are those your own words, or his?" } },
  { speaker: "opp", name: "카이로스", text: { ko: "누구 말이든 계약은 안 바뀐다.", en: "Whoever's words, the contract doesn't change." } },
  { speaker: "player", name: "나", text: { ko: "그 계약 안 받아. 린, 이리 와.", en: "I don't accept that contract. Rin, come here." } },
];

/** STAGE13_KAIROS_OUTRO — 막 IV's finish. All four still standing; 카이로스's
 *  "sacrifice one" 계약 fails and he unravels, repeating that he can't be wrong.
 *  린 starts to blame herself; the player waves it off — everyone made it. The
 *  심장부 방벽 splits open behind 카이로스: 토르 sees the crack, 세라 the bound
 *  figure, 브람 recognises 아르덴 — the seal's 파수꾼, twisted by 벨라. The player
 *  sets the 막 V thesis: help him, not fight him. Villain. */
export const KAIROS_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "카이로스", text: { ko: "이럴 리가… 이럴 순 없어… 내가 틀렸을 리 없어.", en: "This can't be... this can't happen... I can't have been wrong." } },
  { speaker: "opp", name: "린", text: { ko: "미안해요. 나 때문에—", en: "I'm sorry. Because of me—" } },
  { speaker: "player", name: "나", text: { ko: "됐어. 다 무사하잖아.", en: "It's fine. Everyone's safe." } },
  { speaker: "opp", name: "카이로스", text: { ko: "아냐… 이럴 수 없어…", en: "No... this can't be..." } },
  { speaker: "opp", name: "토르", text: { ko: "방벽이 갈라지고 있어.", en: "The wall's splitting open." } },
  { speaker: "opp", name: "세라", text: { ko: "저기 안쪽에 사람이 있어 — 묶여 있는 거 같은데.", en: "There's someone in there — looks like they're chained up." } },
  { speaker: "opp", name: "브람", text: { ko: "저 사람은 아르덴 아니야? 봉인 지키던 파수꾼. 벨라가 저렇게 만들었군.", en: "Isn't that Arden? The guardian who watched the seal. Vela did this to him." } },
  { speaker: "player", name: "나", text: { ko: "저 사람을 도와줘야겠어.", en: "We need to help him." } },
];
